import React from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { observer } from 'mobx-react';

import { YesNoBtnRow } from '@infoods/lifebase-ui';

import classes from './FreeChallengeCreation.module.scss';
import { ChallengeAboutForm } from '../../../../components/form-sections/ChallengeAboutForm/ChallengeAboutForm';
import { ImportantDatesFrom } from '../../../../components/form-sections/ImportantDatesFrom/ImportantDatesFrom';
import { PrizesFrom } from '../../../../components/form-sections/PrizesFrom/PrizesFrom';
import { FaqForm } from '../../../../components/form-sections/FaqForm/FaqForm';
import { ChallengeFormValues } from '../../../../interfaces/ChallengeFormValues';
import { handleDraftSubmit } from '../../utils/handleDraftSubmit';
import { challengeService } from '../../../../services/challengeService';
import { goTo } from '../../../../../shared/utils/angularUtils';
import { convertChallengeData } from '../../utils/convertChallengeData';
import { solutionsStore } from '../../../../../shared/stores/solutionsStore';
import {
  CHALLENGE_BANNER_UPLOAD_TYPE,
  ChallengeBannerUploadType,
} from '../../../../constants/challengeBannerUploadType';
import { openModal } from '../../../../../shared/utils/reactUtils/openModal/openModal';
import { UploadBannerModal } from '../../../../components/form-sections/UploadBannerModal/UploadBannerModal';
import { getChallengeCreateDefaultValues } from '../../utils/getChallengeCreateDefaultValues';
import { openConfirmationModal } from '../../../../../shared/features/confirmation';
import { challengeProfileStore } from '../../../../stores/challengeProfileStore';

const FreeChallengeCreation = observer(() => {
  const { solutionTypeFlags } = solutionsStore;
  const formMethods = useForm<ChallengeFormValues>({
    shouldUnregister: false,
    mode: 'onChange',
    defaultValues: getChallengeCreateDefaultValues(solutionTypeFlags),
  });
  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { dirtyFields },
  } = formMethods;

  const handleUploadBanner = (bannerType: ChallengeBannerUploadType) => {
    const keyConfig = {
      [CHALLENGE_BANNER_UPLOAD_TYPE.WEB]: 'banners.webBanner',
      [CHALLENGE_BANNER_UPLOAD_TYPE.MOBILE]: 'banners.mobileBanner',
    } as const;

    const formKey = keyConfig[bannerType];

    openModal((closeModal) => (
      <UploadBannerModal
        bannerType={bannerType}
        onClose={closeModal}
        onSave={async (banner) => setValue(formKey, banner)}
        onRemove={async () => setValue(formKey, null)}
        getImage={async () => getValues(formKey)}
      />
    ));
  };

  const handlePublish = handleSubmit((data) => {
    const publishChallenge = async () => {
      const payload = convertChallengeData(data);

      const { id } = await challengeService.createChallenge(payload);
      await challengeService.publishChallenge(id);

      challengeProfileStore.setShowPublishedModal(true);
      goTo(`/challenge-profile/${id}`);
    };

    openConfirmationModal({
      title: 'Are you sure you want to publish the challenge?',
      text: 'The challenge will be visible to customers, but they can only join once registration opens. You will be able to unpublish this challenge, and make changes, up until your first customer registers.',
      confirmBtnConfig: {
        text: 'Confirm',
        onClick: publishChallenge,
      },
    });
  });

  const handleSaveDraft = handleDraftSubmit({
    dirtyFields,
    formMethods,
    alwaysValidatePaths: ['about.challengeName', 'importantDates.timeZone'],
    onValid: async (data) => {
      const payload = convertChallengeData(data);

      const { id } = await challengeService.createChallenge(payload);

      goTo(`/challenge-profile/${id}`);
    },
  });

  return (
    <FormProvider {...formMethods}>
      <h1 className={classes.title}>Add a New Challenge</h1>
      <form>
        <ChallengeAboutForm
          mode="EDIT"
          availableSolutionsFlags={solutionTypeFlags}
          onUploadBanner={handleUploadBanner}
        />
        <ImportantDatesFrom mode="EDIT" />
        <PrizesFrom mode="EDIT" />
        <FaqForm mode="EDIT" />
        <YesNoBtnRow
          yesText="Publish"
          yesHandler={handlePublish}
          noText="Save as Draft"
          noHandler={handleSaveDraft}
          className={classes.buttonsContainer}
        />
      </form>
    </FormProvider>
  );
});

export { FreeChallengeCreation };
